// Admin shell sidebar strings — EN + TR.
//
// Wave-1 Day 2 wiring. 35 leaf labels + 8 group headers. AdminShell sidebar
// items array consumes adminShellI18n.items.xxx + .groups.xxx + .title.
//
// Source: features/admin/AdminShell.tsx Faz A IA refactor (2026-05-24).

import type { LocalizedDict } from '../index'

type AdminShellStrings = {
  title: string
  bands: {
    daily: string
    manage: string
    setup: string
  }
  groups: {
    queues: string
    drive: string
    inventory: string
    catalog: string
    designStudio: string
    reviews: string
    sellers: string
  }
  items: {
    overview: string
    servers: string
    orders: string
    driveTriage: string
    producerQueue: string
    stock: string
    stockImport: string
    movements: string
    stockValue: string
    backorders: string
    externalFulfillment: string
    purchaseOrders: string
    returns: string
    products: string
    bundles: string
    measurementQueue: string
    categories: string
    assortment: string
    etsyProfitCalc: string
    etsyResearch: string
    etsyTrends: string
    usCalendar: string
    trainings: string
    designStudioOverview: string
    designStudioCategories: string
    fonts: string
    approvalQueue: string
    mtsFonts: string
    composerPreview: string
    activity: string
    reports: string
    exitPlan: string
    walletDirectory: string
    feesAndCharges: string
    incomingWires: string
    subscriptions: string
    finance: string
    settings: string
    awaitingShipment: string
    allOrders: string
    awaitingApproval: string
    onHold: string
    shipped: string
    samples: string
    bank: string
    companyRegistrations: string
  }
}

export const adminShellI18n: LocalizedDict<AdminShellStrings> = {
  en: {
    title: 'Admin',
    bands: {
      daily: 'Daily',
      manage: 'Manage',
      setup: 'Tools',
    },
    groups: {
      queues: 'Queues',
      drive: 'Drive',
      inventory: 'Inventory',
      catalog: 'Catalog',
      designStudio: 'Design Studio',
      reviews: 'Reviews',
      sellers: 'Sellers',
    },
    items: {
      overview: 'Overview',
      servers: 'Servers',
      orders: 'Orders',
      driveTriage: 'Triage',
      producerQueue: 'Supplier Queue',
      stock: 'Stock',
      stockImport: 'Import / Onboard',
      movements: 'Movements',
      stockValue: 'Stock value',
      backorders: 'Backorder / Shortage',
      externalFulfillment: 'External Fulfillment',
      purchaseOrders: 'Purchase Orders',
      returns: 'Returns',
      products: 'Products',
      bundles: 'Bundles',
      measurementQueue: 'Measurement queue',
      categories: 'Categories',
      assortment: 'Assortment',
      etsyProfitCalc: 'Etsy Profit Calc',
      etsyResearch: 'Etsy Research',
      etsyTrends: 'Trends',
      usCalendar: 'US event calendar',
      trainings: 'Trainings',
      designStudioOverview: 'Overview',
      designStudioCategories: 'Design Categories',
      fonts: 'Fonts',
      approvalQueue: 'Approval Queue',
      mtsFonts: 'Pexify Jewelry Fonts',
      composerPreview: 'Composer (preview)',
      activity: 'Activity',
      reports: 'Reports',
      exitPlan: 'Exit plan',
      walletDirectory: 'Wallet Directory',
      feesAndCharges: 'Fees & charges',
      incomingWires: 'Incoming wires',
      subscriptions: 'Subscriptions',
      finance: 'Finance',
      settings: 'Settings',
      awaitingShipment: 'Awaiting Shipment',
      allOrders: 'All Orders',
      awaitingApproval: 'Awaiting Approval',
      onHold: 'On Hold',
      shipped: 'Shipped',
      samples: 'Samples',
      bank: 'Bank',
      companyRegistrations: 'Company Registrations',
    },
  },
  tr: {
    title: 'Admin',
    bands: {
      daily: 'Günlük',
      manage: 'Yönet',
      setup: 'Araçlar',
    },
    groups: {
      queues: 'Kuyruklar',
      drive: 'Drive',
      inventory: 'Envanter',
      catalog: 'Katalog',
      designStudio: 'Tasarım Stüdyosu',
      reviews: 'Yorumlar',
      sellers: 'Satıcılar',
    },
    items: {
      overview: 'Genel Bakış',
      servers: 'Servers',
      orders: 'Siparişler',
      driveTriage: 'Triyaj',
      producerQueue: 'Tedarikçi Kuyruğu',
      stock: 'Stok',
      stockImport: 'İçe Aktar / Yerleştir',
      movements: 'Hareketler',
      stockValue: 'Stok değeri',
      backorders: 'Ön Sipariş / Eksik Stok',
      externalFulfillment: 'Etiketsiz Gönderim',
      purchaseOrders: 'Satınalma Siparişleri',
      returns: 'İadeler',
      products: 'Ürünler',
      bundles: 'Setler',
      measurementQueue: 'Ölçüm kuyruğu',
      categories: 'Kategoriler',
      assortment: 'Asortiment',
      etsyProfitCalc: 'Etsy Kâr Hesaplayıcı',
      etsyResearch: 'Etsy Araştırma',
      etsyTrends: 'Trendler',
      usCalendar: 'ABD etkinlik takvimi',
      trainings: 'Eğitimler',
      designStudioOverview: 'Genel Bakış',
      designStudioCategories: 'Tasarım Kategorileri',
      fonts: 'Fontlar',
      approvalQueue: 'Onay Kuyruğu',
      mtsFonts: 'Pexify Jewelry Fonts',
      composerPreview: 'Composer (önizleme)',
      activity: 'Aktivite',
      reports: 'Raporlar',
      exitPlan: 'Exit planı',
      walletDirectory: 'Cüzdan Listesi',
      feesAndCharges: 'Ücretler ve kesintiler',
      incomingWires: 'Gelen havaleler',
      subscriptions: 'Abonelikler',
      finance: 'Finans',
      settings: 'Ayarlar',
      awaitingShipment: 'Kargo Bekliyor',
      allOrders: 'Tüm Siparişler',
      awaitingApproval: 'Onay Bekliyor',
      onHold: 'Beklemede',
      shipped: 'Kargoya Verildi',
      samples: 'Numuneler',
      bank: 'Banka',
      companyRegistrations: 'Şirket Kayıtları',
    },
  },
}
